import L from 'leaflet';

/**
 * Tolerance for comparing floating-point coordinates
 */
export const COORDINATE_TOLERANCE = 0.0001;

/**
 * Icon anchor point for markers
 */
export const ICON_ANCHOR: [number, number] = [15, 15];

/**
 * Default fill opacity for drawn shapes
 */
export const DEFAULT_FILL_OPACITY = 0.2;

/**
 * Default color for drawn shapes
 */
export const DEFAULT_SHAPE_COLOR = '#3388ff';

/**
 * Type guard to check if a layer is a Rectangle.
 *
 * @param layer - The layer to check
 * @returns true if layer is a Rectangle, false otherwise
 */
export const isRectangle = (layer: any): layer is L.Rectangle => {
  return layer instanceof L.Rectangle;
};

/**
 * Type guard to check if a layer is a Circle.
 *
 * @param layer - The layer to check
 * @returns true if layer is a Circle, false otherwise
 */
export const isCircle = (layer: any): layer is L.Circle => {
  return layer instanceof L.Circle;
};

/**
 * Type guard to check if a layer is a Polygon.
 *
 * @param layer - The layer to check
 * @returns true if layer is a Polygon, false otherwise
 */
export const isPolygon = (layer: any): layer is L.Polygon => {
  return layer instanceof L.Polygon;
};

/**
 * Type guard to check if a layer is a Marker.
 *
 * @param layer - The layer to check
 * @returns true if layer is a Marker, false otherwise
 */
export const isMarker = (layer: any): layer is L.Marker => {
  return layer instanceof L.Marker;
};

/**
 * Type guard to check if a layer has Geoman (pm) property.
 *
 * @param layer - The layer to check
 * @returns true if layer has pm property, false otherwise
 */
export const hasGeoman = (layer: any): layer is L.Layer & { pm: any } => {
  return layer && typeof layer.pm !== 'undefined';
};

/**
 * Check if a layer is valid for editing (has valid bounds/coordinates).
 *
 * @param layer - The layer to validate
 * @returns true if layer is valid for editing, false otherwise
 */
export const isValidLayer = (layer: any): boolean => {
  if (!layer) {
    return false;
  }

  try {
    // Check if layer has getBounds method and it returns valid bounds
    if (typeof layer.getBounds === 'function') {
      const bounds = layer.getBounds();
      if (bounds && typeof bounds.isValid === 'function' && bounds.isValid()) {
        return true;
      }
    }

    // For markers, check if they have a valid latlng
    if (isMarker(layer) && layer.getLatLng) {
      const latlng = layer.getLatLng();
      return latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number';
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Extract coordinates from a Rectangle layer.
 * Returns [minLng, minLat, maxLng, maxLat] for Box type.
 *
 * @param layer - The Rectangle layer to extract coordinates from
 * @returns Array of coordinates [minLng, minLat, maxLng, maxLat] or null if extraction fails
 */
export const extractRectangleCoords = (layer: L.Rectangle): number[] | null => {
  try {
    const bounds = layer.getBounds();
    if (!bounds || !bounds.isValid()) {
      return null;
    }

    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();

    return [southWest.lng, southWest.lat, northEast.lng, northEast.lat];
  } catch {
    return null;
  }
};

/**
 * Extract coordinates from a Circle layer.
 * Returns [centerLng, centerLat, radius] for Circle type.
 *
 * @param layer - The Circle layer to extract coordinates from
 * @returns Array of coordinates [centerLng, centerLat, radius] or null if extraction fails
 */
export const extractCircleCoords = (layer: L.Circle): number[] | null => {
  try {
    const latlng = layer.getLatLng();
    const radius = layer.getRadius();

    if (!latlng || typeof radius !== 'number') {
      return null;
    }

    return [latlng.lng, latlng.lat, radius];
  } catch {
    return null;
  }
};

/**
 * Extract coordinates from a Polygon layer.
 * Returns [lng1, lat1, lng2, lat2, ...] for Polygon type.
 *
 * @param layer - The Polygon layer to extract coordinates from
 * @returns Array of coordinates [lng1, lat1, lng2, lat2, ...] or null if extraction fails
 */
export const extractPolygonCoords = (layer: L.Polygon): number[] | null => {
  try {
    const latlngs = layer.getLatLngs();
    if (!Array.isArray(latlngs) || latlngs.length === 0) {
      return null;
    }

    // Handle nested arrays (for polygons with holes)
    const flattenLatLngs = (arr: any[]): L.LatLng[] => {
      const result: L.LatLng[] = [];
      for (const item of arr) {
        if (Array.isArray(item)) {
          result.push(...flattenLatLngs(item));
        } else if (item && typeof item.lat === 'number' && typeof item.lng === 'number') {
          result.push(item);
        }
      }
      return result;
    };

    const flatLatLngs = flattenLatLngs(latlngs);
    if (flatLatLngs.length === 0) {
      return null;
    }

    const coords: number[] = [];
    for (const latlng of flatLatLngs) {
      coords.push(latlng.lng, latlng.lat);
    }

    return coords;
  } catch {
    return null;
  }
};

/**
 * Extract coordinates from a Marker layer.
 * Returns [lng, lat] for Marker type.
 *
 * @param layer - The Marker layer to extract coordinates from
 * @returns Array of coordinates [lng, lat] or null if extraction fails
 */
export const extractMarkerCoords = (layer: L.Marker): number[] | null => {
  try {
    const latlng = layer.getLatLng();
    if (!latlng) {
      return null;
    }

    return [latlng.lng, latlng.lat];
  } catch {
    return null;
  }
};

/**
 * Validate coordinates are within reasonable bounds
 * Checks for NaN, Infinity, and out-of-range values
 *
 * @param coords - Array of coordinates to validate
 * @returns true if coordinates are valid, false otherwise
 */
const validateCoordinates = (coords: number[]): boolean => {
  if (!Array.isArray(coords) || coords.length === 0) {
    return false;
  }

  // Check for NaN or Infinity values
  if (coords.some(val => !isFinite(val))) {
    return false;
  }

  // For coordinates with at least 2 values (lat/lng), validate ranges
  if (coords.length >= 2) {
    const [lng, lat] = coords;

    // Validate lat/lng ranges (allow some margin for local coordinate systems)
    // Local coordinates can be in range -1000 to 1000
    if (Math.abs(lat) > 1000 || Math.abs(lng) > 1000) {
      return false;
    }
  }

  return true;
};

/**
 * Extract coordinates from any layer type.
 * Returns the appropriate coordinate format based on layer type.
 *
 * @param layer - The Leaflet layer to extract coordinates from
 * @returns Array of coordinates in format [lng, lat, ...] or null if extraction fails
 *
 * @example
 * const coords = extractLayerCoords(rectangleLayer);
 * // Returns: [minLng, minLat, maxLng, maxLat]
 */
export const extractLayerCoords = (layer: any): number[] | null => {
  if (!layer) {
    return null;
  }

  let coords: number[] | null = null;

  if (isRectangle(layer)) {
    coords = extractRectangleCoords(layer);
  } else if (isCircle(layer)) {
    coords = extractCircleCoords(layer);
  } else if (isPolygon(layer)) {
    coords = extractPolygonCoords(layer);
  } else if (isMarker(layer)) {
    coords = extractMarkerCoords(layer);
  }

  // Validate coordinates before returning
  if (coords && !validateCoordinates(coords)) {
    return null;
  }

  return coords;
};

/**
 * Compare two coordinate arrays for equality (within tolerance).
 *
 * @param coords1 - First coordinate array
 * @param coords2 - Second coordinate array
 * @returns true if coordinates are equal within tolerance, false otherwise
 */
export const compareCoordinates = (coords1: number[], coords2: number[]): boolean => {
  if (!Array.isArray(coords1) || !Array.isArray(coords2)) {
    return false;
  }

  if (coords1.length !== coords2.length) {
    return false;
  }

  for (let i = 0; i < coords1.length; i++) {
    if (Math.abs(coords1[i] - coords2[i]) > COORDINATE_TOLERANCE) {
      return false;
    }
  }

  return true;
};

/**
 * Get shape type from layer instance.
 *
 * @param layer - The layer to get shape type from
 * @returns Shape type ('Rectangle', 'Circle', 'Polygon') or null if not recognized
 */
export const getShapeType = (layer: L.Layer): 'Rectangle' | 'Circle' | 'Polygon' | null => {
  if (isRectangle(layer)) {
    return 'Rectangle';
  }
  if (isCircle(layer)) {
    return 'Circle';
  }
  if (isPolygon(layer)) {
    return 'Polygon';
  }
  return null;
};

/**
 * Clean up a single temporary layer by removing event listeners and the layer itself.
 *
 * @param map - The Leaflet map instance
 * @param layer - The layer to clean up
 */
export const cleanupTemporaryLayer = (map: L.Map, layer: L.Layer): void => {
  try {
    // Remove event listeners before removing
    const marker = isMarker(layer) ? (layer as L.Marker) : null;
    if (marker && marker.dragging) {
      marker.dragging.disable();
    }
    if (layer.off) {
      // Base Leaflet and Geoman listeners cleanup (extended)
      layer.off('dragend');
      layer.off('pm:edit');
      layer.off('pm:update');

      // Added Geoman listeners to avoid leaks and duplicate handlers
      layer.off('pm:markerdragend');
      layer.off('pm:vertexadded');
      layer.off('pm:vertexremoved');

      // Rotation events (Polygon)
      layer.off('pm:rotate');
      layer.off('pm:rotateend');

      // Circle movement and radius/center change events
      layer.off('pm:dragstart');
      layer.off('pm:drag');
      layer.off('pm:dragend');
      layer.off('pm:radiuschange');
      layer.off('pm:radiuschanged');
      layer.off('pm:centerchange');
      layer.off('pm:centerchanged');
      // Geoman marker drag for radius handle and generic change/resize
      layer.off('pm:markerdragstart');
      layer.off('pm:markerdrag');
      layer.off('pm:resize');
      layer.off('pm:change');
      // Fallback Leaflet events
      layer.off('move');
      layer.off('moveend');

      // Stop radius polling fallback if present
      const anyLayer = layer as any;
      if (anyLayer && anyLayer._radiusPoll) {
        try {
          clearInterval(anyLayer._radiusPoll);
        } catch {
          // ignore
        }
        anyLayer._radiusPoll = null;
      }
    }
    if (map.hasLayer(layer)) {
      map.removeLayer(layer);
    }
  } catch (e) {
    // Ignore errors during cleanup
  }
};

/**
 * Clean up all temporary layers from a map.
 * Uses explicit references from WeakMap first, then falls back to iterating through all layers.
 *
 * @param map - The Leaflet map instance
 * @param temporaryLayersRef - WeakMap storing temporary layers by map instance
 */
export const cleanupTemporaryLayers = (
  map: L.Map,
  temporaryLayersRef: WeakMap<L.Map, Set<L.Layer>>
): void => {
  try {
    // First, clean up using explicit references (most reliable)
    const tempLayers = temporaryLayersRef.get(map);
    if (tempLayers) {
      tempLayers.forEach(layer => {
        cleanupTemporaryLayer(map, layer);
      });
      tempLayers.clear();
      temporaryLayersRef.delete(map);
    }

    // Also iterate through map layers as fallback (for any missed layers)
    map.eachLayer((layer: any) => {
      const isTemporary = (layer as any)._isTemporaryDrawingLayer;
      const isMarkerLayer = isMarker(layer);

      if (isTemporary && (layer.pm || isMarkerLayer)) {
        cleanupTemporaryLayer(map, layer);
      }
    });
  } catch (e) {
    // tslint:disable-next-line:no-console
    console.warn('Error cleaning up temporary layers:', e);
  }
};
